import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, LockIcon, CreditCardIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/pixel';
const DAYS_ES = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
export function PricingSection() {
  const [guaranteeOpen, setGuaranteeOpen] = useState(false);
  const [dayName] = useState(() => DAYS_ES[new Date().getDay()]);
  const [startTime] = useState(() => Date.now());
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  useEffect(() => {
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setTimeLeft(Math.max(15 * 60 - elapsed, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);
  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');
  return <section id="pricing" className="py-[44px] md:py-[64px] px-4 bg-[#0F2340] relative overflow-hidden" data-id="element-197">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-198">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="bg-white rounded-xl p-5 md:p-[44px] text-center shadow-2xl relative" data-id="element-199">
          <div className="mb-5 md:mb-6" data-id="element-200">
            {/* Urgency Pill */}
            <div className="flex items-center justify-center mb-5 md:mb-6" data-id="element-201">
              <div className="inline-flex items-center gap-[10px] bg-white border border-[#f0f0f0] rounded-full shadow-sm" style={{
              padding: '6px 14px'
            }} data-id="element-202">
                {/* Green radar dot */}
                <div className="flex items-center justify-center w-3 h-3" data-id="element-203">
                  <span className="relative block w-[7px] h-[7px] rounded-full" style={{
                  background: '#22c55e',
                  boxShadow: '0 0 6px rgba(34, 197, 94, 0.4)'
                }} data-id="element-204">
                    <span className="absolute inset-0 rounded-full" style={{
                    background: '#22c55e',
                    animation: 'mcb-radar 2s infinite ease-in-out'
                  }} data-id="element-205" />
                  </span>
                </div>
                {/* Message */}
                <p className="m-0 whitespace-nowrap uppercase" style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#1a1a1a',
                letterSpacing: '0.8px',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif"
              }} data-id="element-206">
                  OFERTA ACTIVA{' '}
                  <span className="text-[#e0e0e0] font-light mx-1" data-id="element-207">|</span>{' '}
                  <span style={{
                  color: '#DC2626',
                  animation: 'mcb-urgent-blink 1.2s infinite ease-in-out',
                  fontWeight: 900
                }} data-id="element-208">
                    ÚLTIMOS 5 CUPOS DISPONIBLES
                  </span>
                </p>
              </div>
            </div>

            {/* Product Image */}
            <div className="flex justify-center mb-5 md:mb-6" data-id="element-209">
              <img src="https://cdn.magicpatterns.com/uploads/eJe52iqhuRsMuEBWjZSfx1/Mocap_Master_resina.png" alt="Mesas Epoxi Pro — La Guía Maestra" className="w-full max-w-[280px] md:max-w-sm rounded-lg shadow-lg" loading="lazy" width={280} height={280} data-id="element-210" />
            </div>

            {/* Price Display — Horizontal Before/After */}
            <div className="flex items-center justify-center gap-5 md:gap-10" data-id="element-211">
              {/* Before */}
              <div className="flex flex-col items-center" data-id="element-212">
                <span className="text-[10px] md:text-sm font-semibold uppercase tracking-widest text-gray-400 mb-1" data-id="element-213">
                  Antes
                </span>
                <span className="text-xl md:text-4xl text-gray-400 line-through decoration-[#DC2626]/50 decoration-[2px] font-medium font-heading" data-id="element-214">
                  $1.490
                </span>
              </div>

              {/* Divider */}
              <div className="w-px h-12 md:h-20 bg-gray-200" data-id="element-215"></div>

              {/* After */}
              <div className="flex flex-col items-center" data-id="element-216">
                <span className="text-[10px] md:text-sm font-semibold uppercase tracking-widest text-[#15803D] mb-1" data-id="element-217">
                  Ahora
                </span>
                <span className="text-[36px] md:text-[72px] font-bold text-[#1A3A5C] font-heading tracking-tight leading-none" data-id="element-218">
                  $690
                </span>
              </div>
            </div>
          </div>

          {/* Urgency Countdown Banner */}
          <div className="bg-gradient-to-br from-[#C9A84C] via-[#E5C877] to-[#C9A84C] rounded-xl px-6 py-5 md:px-10 md:py-7 max-w-md mx-auto mb-5 md:mb-6 text-center shadow-lg" data-id="element-219">
            <p className="text-[#0F2340] font-extrabold text-lg md:text-xl tracking-wide leading-tight" data-id="element-220">
              OFERTA{' '}
              <span className="underline decoration-[#0F2340] decoration-[3px] underline-offset-[6px]" data-id="element-221">
                HOY {dayName}
              </span>
            </p>
            <p className="text-[#0F2340]/75 text-[13px] md:text-sm mt-2 leading-relaxed max-w-xs mx-auto" data-id="element-222">
              Tu lugar estará reservado durante{' '}
              <strong className="text-[#0F2340]" data-id="element-223">15 minutos</strong>.
              <br data-id="element-224" />
              En caso de no acceder, se cederá el descuento a la siguiente
              persona.
            </p>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-225">
              <div className="flex flex-col items-center" data-id="element-226">
                <span className="bg-[#0F2340] text-white font-extrabold text-[40px] md:text-[56px] leading-none px-4 py-2.5 md:px-5 md:py-3 rounded-lg tabular-nums tracking-tight shadow-md" style={{
                fontFamily: "'Inter', sans-serif"
              }} data-id="element-227">
                  {mins}
                </span>
                <span className="text-[#0F2340]/60 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1.5" data-id="element-228">
                  Min
                </span>
              </div>
              <span className="text-[#0F2340] font-extrabold text-[36px] md:text-[48px] leading-none -mt-4" data-id="element-229">
                :
              </span>
              <div className="flex flex-col items-center" data-id="element-230">
                <span className="bg-[#0F2340] text-white font-extrabold text-[40px] md:text-[56px] leading-none px-4 py-2.5 md:px-5 md:py-3 rounded-lg tabular-nums tracking-tight shadow-md" style={{
                fontFamily: "'Inter', sans-serif"
              }} data-id="element-231">
                  {secs}
                </span>
                <span className="text-[#0F2340]/60 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1.5" data-id="element-232">
                  Seg
                </span>
              </div>
            </div>
          </div>

          <a href="https://resinartesanal.store/cart/51954451415353:1" onClick={trackAddToCart} rel="noopener" className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 md:px-[56px] py-4 md:py-[20px] bg-gradient-to-r from-[#C9A84C] to-[#E5C877] text-[#0F2340] font-bold text-[15px] md:text-[17px] rounded-md transition-transform hover:-translate-y-[3px] shadow-lg hover:shadow-xl mb-5 md:mb-6" data-id="element-233">
            <span data-id="element-234">COMPRAR AHORA</span>
            <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-235" />
          </a>

          {/* Payment Trust Logos */}
          {/* Payment Security & Card Logos */}
          {/* Payment Security & Logos — Boutique Layout */}
          <div className="max-w-[420px] mx-auto rounded-xl border border-gray-100 bg-white px-3 py-[18px] md:px-5 md:py-5 mb-5 md:mb-6" data-id="element-236">
            {/* SSL Badge */}
            <div className="flex items-center justify-center gap-1.5 mb-5" data-id="element-237">
              <LockIcon className="w-[11px] h-[11px] text-[#22C55E] stroke-[3]" data-id="element-238" />
              <span className="text-[9.5px] md:text-[10.5px] font-extrabold text-[#22C55E] uppercase tracking-[0.8px]" data-id="element-239">
                Pago seguro y datos protegidos (SSL)
              </span>
            </div>

            {/* Row 1: International Cards */}
            <div className="flex items-center justify-center gap-[22px] md:gap-6" data-id="element-240">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/98/Visa_Inc._logo_%282005%E2%80%932014%29.svg/3840px-Visa_Inc._logo_%282005%E2%80%932014%29.svg.png" alt="Visa" className="h-[14px] md:h-[16px] w-auto object-contain opacity-85" data-id="element-241" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-[14px] md:h-[16px] w-auto object-contain opacity-85" data-id="element-242" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/f/fd/Maestro_logo.svg" alt="Maestro" className="h-[14px] md:h-[16px] w-auto object-contain opacity-85" data-id="element-243" />
            </div>

            {/* Divider */}
            <div className="h-px bg-gray-100 w-[60%] mx-auto my-[15px]" data-id="element-244"></div>

            {/* Row 2: Mercado Pago + Local Networks */}
            <div className="flex items-center justify-center gap-[18px] md:gap-[22px]" data-id="element-245">
              <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT31TR8PFmP-iF7SmojAXio0OkIcsMGxFVrRw&s" alt="Mercado Pago" className="h-[18px] md:h-[20px] w-auto object-contain opacity-85" data-id="element-246" />
              <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSLZ1qaw-v_lY7oKJ8_AIQ6q0VKtjNwWFlyhA&s" alt="Abitab" className="h-[14px] md:h-[16px] w-auto object-contain opacity-85" data-id="element-247" />
              <img src="https://static.wixstatic.com/media/6bba46_ea504693036941018c7aca2751b79714~mv2.png/v1/fill/w_424,h_164,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/REDPAGOS%20LOGO.png" alt="RedPagos" className="h-[14px] md:h-[16px] w-auto object-contain opacity-85" data-id="element-248" />
            </div>

            {/* Help Text */}
            <p className="text-[10.5px] md:text-[11px] text-[#a0a0a0] mt-[15px] text-center" data-id="element-249">
              Aceptamos tarjetas de crédito, débito y pagos en efectivo
            </p>
          </div>

          {/* Compact Guarantee Block */}
          <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg max-w-md mx-auto overflow-hidden" data-id="element-250">
            <button onClick={() => setGuaranteeOpen(!guaranteeOpen)} className="w-full flex items-center justify-between px-4 py-3 md:px-5 md:py-3.5 hover:bg-[#DCFCE7]/30 transition-colors" data-id="element-251">
              <div className="flex items-center gap-2" data-id="element-252">
                <ShieldCheckIcon className="w-5 h-5 text-[#15803D]" data-id="element-253" />
                <span className="text-sm md:text-base font-bold text-[#15803D]" data-id="element-254">
                  Garantía de 7 días
                </span>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-[#15803D] transition-transform duration-300 ${guaranteeOpen ? 'rotate-180' : ''}`} data-id="element-255" />
            </button>
            <AnimatePresence initial={false} data-id="element-256">
              {guaranteeOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-257">
                  <div className="px-4 pb-4 pt-1 md:px-5 md:pb-5 md:pt-1 text-center" data-id="element-258">
                    <p className="text-sm md:text-body-main text-gray-600 leading-relaxed" data-id="element-259">
                      Si no cumplió lo que prometió, te devolvemos el{' '}
                      <strong className="text-[#16A34A]" data-id="element-260">
                        100% de tu dinero
                      </strong>
                      . Devolución total{' '}
                      <strong className="text-[#16A34A]" data-id="element-261">sin preguntas</strong>.
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>;
}